import { useState, useEffect } from 'react';
import { FiMail, FiRefreshCw, FiX, FiFilter, FiClock } from 'react-icons/fi';
import { organizationAPI, type Invitation } from '../../api/organizations';
import { Card } from '../ui/Card/Card';
import { Button } from '../ui/Button/Button';
import { Badge } from '../ui/Badge/Badge';
import { useToast } from '../../hooks/useToast';
import { ConfirmModal } from '../Modal/Modal';
import styles from './InvitationsList.module.css';

interface InvitationsListProps {
  organizationId: string;
  onRefresh?: () => void;
  heading?: string;
  // Bump this (e.g. a counter) to force a reload from a parent that just
  // caused a mutation this component wouldn't otherwise know about - see
  // TeamMembers.tsx's handleInvite, which bumps it after every successful
  // invite. Purely a dependency-array trigger; the value itself is never read.
  refreshKey?: number;
}

export function InvitationsList({ organizationId, onRefresh, heading, refreshKey }: InvitationsListProps) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { addToast } = useToast();
  // P1.2: in-app confirm dialog in place of window.confirm().
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string } | null>(null);

  useEffect(() => {
    if (organizationId) {
      loadInvitations();
    }
    // loadInvitations is intentionally excluded; it closes over the same deps
    // we list. refreshKey has no meaning of its own - bumping it is how a
    // parent forces a reload after a mutation this component didn't cause.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, statusFilter, refreshKey]);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 50, offset: 0 };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await organizationAPI.getInvitations(organizationId, params);
      setInvitations(response.invitations);
    } catch (error: any) {
      addToast(error.message || 'Failed to load invitations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (inviteId: string, email: string) => {
    try {
      setActionLoading(inviteId);
      await organizationAPI.resendInvitation(organizationId, inviteId);
      addToast(`Invitation resent to ${email}`, 'success');
      loadInvitations();
      onRefresh?.();
    } catch (error: any) {
      addToast(error.message || 'Failed to resend invitation', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    const { id: inviteId, email } = revokeTarget;

    try {
      setActionLoading(inviteId);
      await organizationAPI.revokeInvitation(organizationId, inviteId);
      addToast(`Invitation revoked for ${email}`, 'success');
      loadInvitations();
      onRefresh?.();
    } catch (error: any) {
      addToast(error.message || 'Failed to revoke invitation', 'error');
    } finally {
      setActionLoading(null);
      setRevokeTarget(null);
    }
  };

  const getStatusBadgeVariant = (status: string): 'warning' | 'success' | 'error' | 'secondary' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'success';
      case 'expired': return 'error';
      case 'revoked': return 'secondary';
      default: return 'secondary';
    }
  };

  const getRoleBadgeVariant = (role: string): 'primary' | 'secondary' | 'info' | 'tertiary' => {
    switch (role) {
      case 'admin': return 'info';
      case 'member': return 'secondary';
      case 'viewer': return 'tertiary';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const hoursUntilExpiry = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilExpiry < 24 && hoursUntilExpiry > 0;
  };

  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');

  return (
    <Card className={styles.invitationsCard}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <FiMail className={styles.headerIcon} />
          <div>
            <h3>{heading ?? 'Pending Invitations'}</h3>
            <p className={styles.subtitle}>
              {pendingInvitations.length} pending invitation{pendingInvitations.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className={styles.filterSection}>
          <FiFilter className={styles.filterIcon} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading invitations...</div>
      ) : invitations.length === 0 ? (
        <div className={styles.empty}>
          <FiMail className={styles.emptyIcon} />
          <p>No invitations found</p>
        </div>
      ) : (
        <div className={styles.invitationsList}>
          {invitations.map((invitation) => (
            <div key={invitation.id} className={styles.invitationItem}>
              <div className={styles.invitationInfo}>
                <div className={styles.invitationHeader}>
                  <span className={styles.email}>{invitation.email}</span>
                  <div className={styles.badges}>
                    <Badge variant={getRoleBadgeVariant(invitation.role)} size="sm">
                      {invitation.role}
                    </Badge>
                    <Badge variant={getStatusBadgeVariant(invitation.status)} size="sm">
                      {invitation.status}
                    </Badge>
                  </div>
                </div>

                <div className={styles.invitationDetails}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Invited by:</span>
                    <span className={styles.detailValue}>
                      {`${invitation.invited_by.first_name} ${invitation.invited_by.last_name}`.trim()}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Invited:</span>
                    <span className={styles.detailValue}>{formatDate(invitation.invited_at)}</span>
                  </div>
                  {invitation.status === 'pending' && (
                    <div className={styles.detailItem}>
                      <FiClock className={styles.clockIcon} />
                      <span className={`${styles.detailValue} ${isExpiringSoon(invitation.expires_at) ? styles.expiringSoon : ''}`}>
                        Expires {formatDate(invitation.expires_at)}
                      </span>
                    </div>
                  )}
                  {invitation.accepted_at && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Accepted:</span>
                      <span className={styles.detailValue}>{formatDate(invitation.accepted_at)}</span>
                    </div>
                  )}
                </div>


              </div>

              {invitation.status === 'pending' && (
                <div className={styles.actions}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleResend(invitation.id, invitation.email)}
                    disabled={actionLoading === invitation.id}
                  >
                    <FiRefreshCw className={actionLoading === invitation.id ? styles.spinning : ''} />
                    Resend
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setRevokeTarget({ id: invitation.id, email: invitation.email })}
                    disabled={actionLoading === invitation.id}
                  >
                    <FiX />
                    Revoke
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={confirmRevoke}
        title="Revoke invitation"
        message={
          revokeTarget
            ? `Are you sure you want to revoke the invitation for ${revokeTarget.email}?`
            : ''
        }
        confirmText="Revoke"
        cancelText="Cancel"
        variant="danger"
      />
    </Card>
  );
}


